import type { FC } from 'react';

import { groundCircle, project } from 'src/tv/live/MapFrame';
import { MAP_HEIGHT, MAP_WIDTH, projectLonLat } from 'src/tv/live/nordicOutlines';
import type { Visit } from 'src/tv/live/useLiveFeed';
import { TV_COLORS } from 'src/tv/tvTheme';

const APPEAR_MS = 800;
const RING_MS = 3200;
const FADE_MS = 3000;
/** How long a visit stays on the map, from appearing to faded out, unless a screen sets its own. */
export const VISIT_LIFETIME_MS = APPEAR_MS + 12_000 + FADE_MS;
const PIN_HEIGHT = 5;
const BADGE_RADIUS = 2.1;
const ICON_SIZE = 2.8;

// MUI's Fingerprint and TaskAlt (the list's icons), drawn inside the map's SVG.
const ICON_PATHS = {
  identified: 'M17.81 4.47c-.08 0-.16-.02-.23-.06C15.66 3.42 14 3 12.01 3c-1.98 0-3.86.47-5.57 1.41-.24.13-.54.04-.68-.2-.13-.24-.04-.55.2-.68C7.82 2.52 9.86 2 12.01 2c2.13 0 3.99.47 6.03 1.52.25.13.34.43.21.67-.09.18-.26.28-.44.28M3.5 9.72c-.1 0-.2-.03-.29-.09-.23-.16-.28-.47-.12-.7.99-1.4 2.25-2.5 3.75-3.27C9.98 4.04 14 4.03 17.15 5.65c1.5.77 2.76 1.86 3.75 3.25.16.22.11.54-.12.7s-.54.11-.7-.12c-.9-1.26-2.04-2.25-3.39-2.94-2.87-1.47-6.54-1.47-9.4.01-1.36.7-2.5 1.7-3.4 2.96-.08.14-.23.21-.39.21m6.25 12.07c-.13 0-.26-.05-.35-.15-.87-.87-1.34-1.43-2.01-2.64-.69-1.23-1.05-2.73-1.05-4.34 0-2.97 2.54-5.39 5.66-5.39s5.66 2.42 5.66 5.39c0 .28-.22.5-.5.5s-.5-.22-.5-.5c0-2.42-2.09-4.39-4.66-4.39s-4.66 1.97-4.66 4.39c0 1.44.32 2.77.93 3.85.64 1.15 1.08 1.64 1.85 2.42.19.2.19.51 0 .71-.11.1-.24.15-.37.15m7.17-1.85c-1.19 0-2.24-.3-3.1-.89-1.49-1.01-2.38-2.65-2.38-4.39 0-.28.22-.5.5-.5s.5.22.5.5c0 1.41.72 2.74 1.94 3.56.71.48 1.54.71 2.54.71.24 0 .64-.03 1.04-.1.27-.05.53.13.58.41.05.27-.13.53-.41.58-.57.11-1.07.12-1.21.12M14.91 22c-.04 0-.09-.01-.13-.02-1.59-.44-2.63-1.03-3.72-2.1-1.4-1.39-2.17-3.24-2.17-5.22 0-1.62 1.38-2.94 3.08-2.94s3.08 1.32 3.08 2.94c0 1.07.93 1.94 2.08 1.94s2.08-.87 2.08-1.94c0-3.77-3.25-6.83-7.25-6.83-2.84 0-5.44 1.58-6.61 4.03-.39.81-.59 1.76-.59 2.8 0 .78.07 2.01.67 3.61.1.26-.03.55-.29.64-.26.1-.55-.04-.64-.29-.49-1.31-.73-2.61-.73-3.96 0-1.2.23-2.29.68-3.24 1.33-2.79 4.28-4.6 7.51-4.6 4.55 0 8.25 3.51 8.25 7.83 0 1.62-1.38 2.94-3.08 2.94s-3.08-1.32-3.08-2.94c0-1.07-.93-1.94-2.08-1.94s-2.08.87-2.08 1.94c0 1.71.66 3.31 1.87 4.51.95.94 1.86 1.46 3.27 1.85.27.07.42.35.35.61-.05.23-.26.38-.47.38',
  submitted: 'M22 5.18 10.59 16.6l-4.24-4.24 1.41-1.41 2.83 2.83 10-10zm-2.21 5.04c.13.57.21 1.17.21 1.78 0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8c1.58 0 3.04.46 4.28 1.25l1.44-1.44C16.1 2.67 14.13 2 12 2 6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10c0-1.19-.22-2.33-.6-3.39z',
};

const toPlace = (visit: Visit) => {
  const [x, y] = projectLonLat(visit.lon, visit.lat);

  return { name: visit.id, x, y };
};

const isOnMap = (visit: Visit) => {
  const { x, y } = toPlace(visit);

  return x >= 0 && x <= MAP_WIDTH && y >= 0 && y <= MAP_HEIGHT;
};

/**
 * A soft ping: the dot fades in and two rings spread slowly on the ground, and a pin rises from it with the
 * visit's icon (BankID or submitted), like the offices' beacons, so a visitor stands out next to the calls.
 */
const VisitMark: FC<{ visit: Visit; now: number; color: string; isNewest: boolean; lifetimeMs: number }> = (props) => {
  const { visit, now, color, isNewest, lifetimeMs } = props;
  const place = toPlace(visit);
  const age = now - visit.receivedAt;
  const isSubmitted = visit.kind === 'submitted';
  const appear = Math.min(1, age / APPEAR_MS);
  const fadeFrom = lifetimeMs - FADE_MS;
  const opacity = age < fadeFrom ? appear : Math.max(0, 1 - (age - fadeFrom) / FADE_MS);
  const reach = isSubmitted ? 6 : 4;
  const [baseX, baseY] = project(place.x, place.y);
  // Rises with a small overshoot as the dot appears.
  const rise = appear < 1 ? 1 - (1 - appear) ** 3 * Math.cos(appear * Math.PI * 1.5) : 1;
  const [x, y] = project(place.x, place.y, PIN_HEIGHT * rise);

  return (
    <g opacity={opacity}>
      {[0, RING_MS / 2].map((offset) => {
        const ring = ((age + offset) % RING_MS) / RING_MS;

        return (
          <path
            key={offset}
            d={groundCircle(place, 0.8 + ring * reach)}
            fill="none"
            stroke={color}
            strokeWidth={0.3}
            opacity={(1 - ring) * 0.8}
          />
        );
      })}
      <path
        d={groundCircle(place, isSubmitted ? 1.2 : 0.9)}
        fill={color}
        opacity={0.6}
        filter="url(#tv-map-glow)"
      />
      <path
        d={groundCircle(place, isSubmitted ? 0.6 : 0.45)}
        fill="#fff"
      />
      <line
        x1={baseX}
        y1={baseY}
        x2={x}
        y2={y}
        stroke={color}
        strokeWidth={0.35}
      />
      <circle
        cx={x}
        cy={y}
        r={BADGE_RADIUS}
        fill={TV_COLORS.background}
        stroke={color}
        strokeWidth={0.35}
      />
      <svg
        x={x - ICON_SIZE / 2}
        y={y - ICON_SIZE / 2}
        width={ICON_SIZE}
        height={ICON_SIZE}
        viewBox="0 0 24 24"
      >
        <path
          d={ICON_PATHS[visit.kind]}
          fill={color}
        />
      </svg>
      {/* Only the newest is named: towns around Oslo sit too close for two labels. */}
      {isNewest && visit.city && (
        <text
          x={x + BADGE_RADIUS + 1}
          y={y + 0.9}
          fill={TV_COLORS.text}
          fontSize={2.6}
          fontWeight={600}
        >
          {visit.city}
        </text>
      )}
    </g>
  );
};

/** The visits that arrived live, as pings; the backlog sent on connect is only for the list and counts. */
export const VisitLayer: FC<{ visits: Visit[]; now: number; color: string; lifetimeMs: number }> = (props) => {
  const { visits, now, color, lifetimeMs } = props;
  const live = visits.filter((visit) => visit.isLive && isOnMap(visit) && now - visit.receivedAt < lifetimeMs);

  // Oldest first, so the newest pin and its label are drawn on top: a visitor's submit lands on their own BankID pin.
  return (
    <>
      {[...live].reverse().map((visit) => <VisitMark
        key={visit.id}
        visit={visit}
        now={now}
        color={color}
        isNewest={visit === live[0]}
        lifetimeMs={lifetimeMs}
      />)}
    </>
  );
};
